import { Card, CardContent, Typography } from '@mui/material';
import type { Token } from '@stripe/stripe-js';
import dayjs from 'dayjs';
import React, { useState } from 'react';
import { defineMessages, useIntl } from 'react-intl';

import PaymentModalContent from '@components/Modal/Content/PaymentModalContent';

import { RoleName } from '@constants/role_name';
import * as ROUTES from '@constants/routes';

import type { CostCalculatorCosts } from '@hooks/useCostCalculator';
import { useModal } from '@hooks/useModal';
import { useNavigate } from '@hooks/useNavigate';
import { useSession } from '@hooks/useSession';
import { useSnackbar } from '@hooks/useSnackbar';

import type { OnboardingMember } from '@models/onboarding-member';
import { pickAnsweredResponses } from '@models/onboarding-member';
import { toPhoneContactInputs } from '@models/patient';
import { DEMOGRAPHICS_QUESTIONNAIRE_IDENTIFIER } from '@models/questionnaire';

import { formatName } from '@utils/format';

import {
  AccountType,
  type AccountOwnerInput,
  type CreateUserModelInput,
  type StripeCoupon,
  type TaxRate,
} from 'generated/types';
import { useRegisterAccount } from 'generated/user.generated';

import AccountInformation from '../Organization/AccountInformation';
import AccountSummary from '../Organization/AccountSummary';
import type { AccountValues } from '../Organization/types';
import { EmailInvitation } from './EmailInvitation';
import NewAccountFormCollection, {
  defaultMember,
} from './NewAccountFormCollection';
import PaymentInformation from './PaymentInformation';
import PaymentSummary from './PaymentSummary';

const messages = defineMessages({
  title: {
    id: 'newAccount.title',
    defaultMessage: 'New Account',
  },
  registerFailed: {
    id: 'GLOBAL.error',
    defaultMessage: 'An error occurred. Please try again later.',
  },
  loadingCustomer: {
    id: 'newAccount.loadingCustomer',
    defaultMessage: 'Creating customer',
  },
  loadingSubscriptions: {
    id: 'newAccount.loadingSubscriptions',
    defaultMessage: 'Creating subscriptions',
  },
  loadingPrimary: {
    id: 'newAccount.loadingPrimary',
    defaultMessage: 'Setting up primary user',
  },
  loadingSecondary: {
    id: 'newAccount.loadingSecondary',
    defaultMessage: "Setting up {name}'s account.",
  },
  paymentProcessingFailed: {
    id: 'newAccount.paymentProcessingFailed',
    defaultMessage: 'Invoice could not be processed, payment failed',
  },
  tokenMissing: {
    id: 'newAccount.stripeTokenMissing',
    defaultMessage: 'Unable to facilitate payment',
  },
  accountInfo: {
    id: 'newAccount.accountInfo',
    defaultMessage: 'Enter the account information',
  },
  leadingCaption: {
    id: 'newAccount.leadingCaption',
    defaultMessage:
      'Any users you create here will be added to the same account. Examples of users added to the same account are: Families, common-law partners, and caregivers and their patients. If you want to add users to different accounts, a separate account should be created.',
  },
});

type SetupPayment = {
  name: string;
  phone: string;
  line1: string;
  line2: string;
  city: string;
  country?: string;
  postalCode: string;
  province?: string;
  couponCode: string;
  coupon: StripeCoupon | null;
  token: Token | null;
};

const initialPayment: SetupPayment = {
  name: '',
  phone: '',
  line1: '',
  line2: '',
  city: '',
  country: 'CA',
  postalCode: '',
  province: undefined,
  couponCode: '',
  coupon: null,
  token: null,
};

const initialAccount: AccountValues = {
  name: '',
  phone: '',
  line1: '',
  line2: '',
  city: '',
  country: undefined,
  postalCode: '',
  province: undefined,
};

interface NewAccountProps {
  // Optional. ID of the parent to add this account under.
  parent_id?: string | null;
}

const NewAccount: React.FC<NewAccountProps> = ({ parent_id }) => {
  const { formatMessage } = useIntl();
  const [registerUserAccount] = useRegisterAccount();
  const { enqueueSnackbar } = useSnackbar();
  const { toggleModal, openModal } = useModal();
  const { navigateRoute } = useNavigate();
  const {
    state: { isVitall },
  } = useSession();

  const [step, setStep] = useState(0);
  const [sendInvite, setSendInvite] = useState(false);
  const [members, setMembers] = useState<OnboardingMember[]>([
    { ...defaultMember },
  ]);
  const [payment, setPayment] = useState<SetupPayment>({
    ...initialPayment,
  });
  const [loadingStep, setLoadingStep] = useState('');

  const isOrgManaged = Boolean(parent_id);
  const [account, setAccount] = useState<AccountValues>({
    ...initialAccount,
  });

  function handleSubmitPlanMembers(values: { members: OnboardingMember[] }) {
    setMembers(values.members);
    const accountOwner = values.members.at(0);
    // Pre-populate the account name with the first member's name
    setAccount((prev) => ({
      ...prev,
      ...(accountOwner && {
        name: formatName(accountOwner.firstName, accountOwner.lastName) ?? '',
      }),
    }));
    setStep(1);
  }

  function handleSubmitPaymentDetails(values: SetupPayment) {
    setPayment(values);
    setStep(2);
  }
  function handleSubmitAccountInfo(values: AccountValues) {
    setAccount(values);
    setStep(2);
  }

  async function handleSubmit(billing?: {
    costs: CostCalculatorCosts;
    taxRate: TaxRate | null;
  }) {
    const currency = billing?.costs.currency;

    if (!isOrgManaged && !payment.token) {
      enqueueSnackbar({
        variant: 'error',
        message: formatMessage(messages.tokenMissing),
      });
      return;
    }

    // Create account + primary user + secondary users
    setLoadingStep(formatMessage(messages.loadingPrimary));
    const primaryUser = members[0];
    const secondaryUsersArray = members.slice(1);
    function mapPatient(user: OnboardingMember) {
      const answeredQuestionnaireResponses = pickAnsweredResponses(
        user.questionnaireResponses
      );
      const phones = toPhoneContactInputs(user.phones);

      const userInput: CreateUserModelInput = {
        email: user.email?.toLowerCase(),
        first_name: user.firstName,
        middle_name: user.middleName,
        last_name: user.lastName,
        toc_completed: user.consents?.terms,
        pn_completed: user.consents?.terms,
        casl_completed: user.consents?.contact,
        // is_patient: user.isPatient,
        // TODO Extend to other roles
        roles: [user.isPatient ? RoleName.Patient : RoleName.Client],
        // Email invites are suppressed if this is false
        send_invite: sendInvite,
        // Connect user to plan
        plan_id: user.planId,
        patientInputs: {
          date_of_birth: user.dateOfBirth
            ? dayjs(user.dateOfBirth).format('YYYY-MM-DD')
            : null,
          ...(phones && { phones }),
          ...(Object.keys(answeredQuestionnaireResponses).length > 0 && {
            questionnaire: {
              identifier: DEMOGRAPHICS_QUESTIONNAIRE_IDENTIFIER,
              input: answeredQuestionnaireResponses,
            },
          }),
        },
      };
      return userInput;
    }
    // The owner is members[0] and secondary members are the rest of the same
    // array, both from the same member form (NewAccountFormCollection), so
    // every patient's phone comes from their own field, never from the
    // account/payment step — that's the organization's or the cardholder's
    // contact number, not the patient's (see AccountInformation's
    // phone_number wiring below).
    const owner = mapPatient(primaryUser) as AccountOwnerInput;
    const secondaryUsers: CreateUserModelInput[] = secondaryUsersArray.map(
      (user) => mapPatient(user)
    );

    let registerAccountResponse;
    try {
      registerAccountResponse = await registerUserAccount({
        variables: {
          input: {
            owner,
            members: secondaryUsers,
            currency,
            // Omit payment if org-managed account. Payment handled by org itself.
            stripe_payment: isOrgManaged
              ? null
              : {
                  source: payment.token?.id || '',
                  name: payment.name,
                  phone: payment.phone || '',
                  line1: payment.line1,
                  line2: payment.line2,
                  city: payment.city,
                  country: payment.country ?? '',
                  postal_code: payment.postalCode,
                  state: payment.province ?? '',
                },
            // Persist the account's address/phone. When org-managed, this comes
            // from the AccountInformation step; otherwise from the payment step.
            ...(isOrgManaged
              ? {
                  phone_number: account.phone,
                  billing_address: account.line1,
                  billing_address2: account.line2,
                  city: account.city,
                  country_id: account.country,
                  region_id: account.province,
                  zip_postal: account.postalCode,
                }
              : {
                  phone_number: payment.phone,
                  billing_address: payment.line1,
                  billing_address2: payment.line2,
                  city: payment.city,
                  country_id: payment.country,
                  region_id: payment.province,
                  zip_postal: payment.postalCode,
                }),
            account_type: AccountType.individual,
            // Register this account as a child of parent ID, if provided
            parent_id,
          },
        },
      });
    } catch {
      // A GraphQL error rejects under Apollo's default errorPolicy, so without
      // this the rejection escapes and AccountSummary's caller never recovers.
      enqueueSnackbar({
        variant: 'error',
        message: formatMessage(messages.registerFailed),
      });
      return;
    }

    const primaryAccount = registerAccountResponse.data?.registerAccount;
    const primaryAccountId = primaryAccount?.account.account_id;
    const primaryUserId = primaryAccount?.user.user_id;
    if (!primaryAccount || !primaryAccountId || !primaryUserId) {
      // `errors` can be an empty array: optional chaining guards `errors` being
      // nullish, not `errors[0]` being undefined.
      const reason = registerAccountResponse.errors?.[0]?.message.replace(
        /^(owner\.|members\.)/,
        ''
      );
      enqueueSnackbar({
        variant: 'error',
        message: reason || formatMessage(messages.registerFailed),
      });
      return;
    }

    openModal({
      width: 400,
      children: (
        <PaymentModalContent
          didPay={Boolean(payment.token)}
          didSendEmail={sendInvite}
          inputCallback={toggleModal}
        />
      ),
    });

    // Go back to parent org, falling back to created account if no org
    navigateRoute(
      isVitall
        ? `${ROUTES.ACCOUNTS}${parent_id ?? primaryAccount.account.account_id}`
        : `${ROUTES.PATIENTS}`
    );
  }

  function handleBack() {
    setStep((step) => step - 1);
  }

  return (
    <>
      {step === 0 && (
        <>
          <Typography variant="caption">
            {formatMessage(messages.leadingCaption)}
          </Typography>
          <EmailInvitation sendInvite={sendInvite} onChange={setSendInvite} />
          <Card>
            <CardContent>
              <NewAccountFormCollection
                initialMembers={members}
                onSubmit={handleSubmitPlanMembers}
                parentProps={
                  parent_id ? { parent_id, shouldFetchPlans: true } : undefined
                }
              />
            </CardContent>
          </Card>
        </>
      )}
      {step === 1 &&
        (isOrgManaged ? (
          <>
            <Typography variant="overline">
              {formatMessage(messages.accountInfo)}
            </Typography>
            <AccountInformation
              initialValues={account}
              onBack={handleBack}
              onSubmit={handleSubmitAccountInfo}
            />
          </>
        ) : (
          <Card>
            <CardContent>
              <PaymentInformation
                initialPayment={payment}
                onBack={handleBack}
                onSubmit={handleSubmitPaymentDetails}
              />
            </CardContent>
          </Card>
        ))}
      {step === 2 &&
        (isOrgManaged ? (
          <AccountSummary
            account={account}
            members={members}
            onBack={handleBack}
            onSubmit={handleSubmit}
            isAddForm={false}
            parent_id={parent_id}
          />
        ) : (
          <Card>
            <CardContent>
              <PaymentSummary
                loadingStep={loadingStep}
                members={members}
                onBack={handleBack}
                onSubmit={handleSubmit}
                payment={payment}
              />
            </CardContent>
          </Card>
        ))}
    </>
  );
};

export default NewAccount;
export type { SetupPayment };
